import React, { FC, useCallback, memo } from 'react';
import styled, { css } from 'styled-components';
import { DropdownToggle } from 'reactstrap';
import { DropdownWithChildrenProps, DropdownWithChildrenContextType, TextWrapOption } from '../shared/types';
import { StyledClearIcon } from '../shared/styles';
import DropdownWithChildrenSelectionsResult from '../DropdownWithChildrenSelectionsResult';

const StyledDropdownToggle = styled(DropdownToggle)<{
  $isSomeDropdownOptionsSelected: boolean;
  $isToggleLabelExist: boolean;
  $isSelectionsResultExist: boolean;
  $textWrap?: TextWrapOption;
}>`
  align-items: center;
  background-color: ${(props): string =>
    props.$isSomeDropdownOptionsSelected
      ? props.theme.form.state.selected.backgroundColor
      : props.theme.form.backgroundColor};
  border: 0.0625rem solid ${(props): string => props.theme.form.borderColor};
  border-radius: 0.25rem;
  color: ${(props): string => props.theme.typography.body.color};
  display: flex;
  padding: 0.5rem;
  padding-right: 1.75rem;
  position: relative;
  font-size: 0.875rem;
  min-height: 2rem;
  line-height: 1;
  justify-content: space-between;
  ${(props) =>
    props.$textWrap
      ? css`
          text-wrap: ${props.$textWrap};
        `
      : null}

  ${(props) =>
    props.$isToggleLabelExist || props.$isSelectionsResultExist
      ? `justify-content: space-between;`
      : `justify-content: flex-end;`}

  ${(props) => `font-weight: ${props.$isSelectionsResultExist ? 'normal' : '500'};`}

  &:after {
    border-top: 0.375rem solid;
    border-right: 0.375rem solid transparent;
    border-left: 0.375rem solid transparent;
    vertical-align: middle;
    position: absolute;
    right: 0.5rem;
  }
`;

interface Props {
  isToggleLabelExist: boolean;
  isChildrensContentFilled: DropdownWithChildrenProps['isChildrensContentFilled'];
  isSomeDropdownOptionsSelected: DropdownWithChildrenContextType['isSomeDropdownOptionsSelected'];
  isSelectionsResultExist: DropdownWithChildrenContextType['isSelectionsResultExist'];
  isDisabled: DropdownWithChildrenProps['isDisabled'];
  isClearable: DropdownWithChildrenProps['isClearable'];
  withSelectionsResult: DropdownWithChildrenProps['withSelectionsResult'];
  selectionsResult: DropdownWithChildrenContextType['selectionsResult'];
  selectionsResultType: DropdownWithChildrenProps['selectionsResultType'];
  id: DropdownWithChildrenProps['id'];
  toggleLabel: DropdownWithChildrenProps['toggleLabel'];
  handleSelectedOptionsClear: DropdownWithChildrenContextType['handleSelectedOptionsClear'];
  onOptionsFetch: DropdownWithChildrenProps['onOptionsFetch'];
  dataTestId: DropdownWithChildrenProps['dataTestId'];
  qaId?: string;
  textWrap?: TextWrapOption;
}

const DropdownWithChildrenTogglerButton: FC<Props> = ({
  isToggleLabelExist,
  isChildrensContentFilled,
  isSomeDropdownOptionsSelected,
  isSelectionsResultExist,
  isDisabled,
  isClearable,
  withSelectionsResult,
  selectionsResult,
  selectionsResultType,
  id,
  toggleLabel,
  handleSelectedOptionsClear,
  onOptionsFetch,
  dataTestId,
  qaId,
  textWrap,
}) => {
  const handleClick = useCallback(() => {
    if (isDisabled) return;

    onOptionsFetch?.();
  }, [isDisabled]);

  return (
    <StyledDropdownToggle
      $isSomeDropdownOptionsSelected={isToggleLabelExist && (isChildrensContentFilled || isSomeDropdownOptionsSelected)}
      $isToggleLabelExist={isToggleLabelExist}
      $isSelectionsResultExist={isSelectionsResultExist}
      caret
      tag="div"
      role="button"
      id={id}
      data-testid={`${dataTestId}-toggler-button`}
      data-qa-id={`${dataTestId}-toggler-button`}
      onClick={handleClick}
      aria-label={toggleLabel}
      $textWrap={textWrap}
    >
      {withSelectionsResult ? (
        <DropdownWithChildrenSelectionsResult
          isPositionAbsolute={false}
          selectionsResult={selectionsResult}
          selectionsResultType={selectionsResultType}
        />
      ) : (
        toggleLabel
      )}
      {isSelectionsResultExist && isClearable && (
        <StyledClearIcon name="close" inheritColor onClick={handleSelectedOptionsClear} />
      )}
    </StyledDropdownToggle>
  );
};

export default memo(DropdownWithChildrenTogglerButton);
